import type { ShellSpawnOptions, ShellSpawnResult } from './types.js';
import { DEFAULT_PREVIEW_ENVIRONMENT_PROCESS_LIFECYCLE } from '../process-lifecycle.js';
import { devNullReadFd } from './utils.js';
import { trySpawnPipedViaSh } from './try-spawn-piped-via-sh.js';
import { trySpawnShellTruePiped } from './try-spawn-shell-true-piped.js';
import { trySpawnMergedLogFile } from './try-spawn-merged-log-file.js';
import {
  trySpawnShellScriptLogRedirectUnix,
  trySpawnShellScriptLogRedirectWin,
} from './try-spawn-shell-script-log-redirect.js';
import { trySpawnInheritStdio } from './try-spawn-inherit.js';

/**
 * Prefer piped stdout/stderr for log tails + firehose/browser forwarding.
 *
 * When `stdio: 'pipe'` works, chunks are true in-memory streams (no disk). File + shell-script
 * fallbacks exist because some hosts throw sync EBADF for pipe pairs; those write to a temp file
 * and the CLI polls it — there is no separate in-memory workaround without fixing the runtime
 * (e.g. different Node build) or a native helper (e.g. PTY).
 *
 * Order:
 * 1. Piped stdout/stderr via `/bin/sh -c` (or cmd) — devnull / ignore / full pipe stdin variants.
 * 2. `spawn(command, { shell: true })` + devnull stdin + pipes.
 * 3. Same command with stdio fds opened on a temp log file.
 * 4. Wrapper shell script on disk + `stdio: 'ignore'`.
 * 5. `inherit` — last resort.
 */
export function shellSpawn(
  command: string,
  env: NodeJS.ProcessEnv,
  cwd: string,
  options?: ShellSpawnOptions,
): ShellSpawnResult {
  const signal = options?.signal;
  const lifecycle = options?.lifecycle ?? DEFAULT_PREVIEW_ENVIRONMENT_PROCESS_LIFECYCLE;
  const piped = trySpawnPipedViaSh(command, env, cwd, signal, lifecycle);
  if (piped.ok) {
    return piped.result;
  }
  let lastErr = piped.lastErr;

  const shellTrueProc = trySpawnShellTruePiped(command, env, cwd, devNullReadFd(), signal, lifecycle);
  if (shellTrueProc) {
    return { proc: shellTrueProc, pipedStdoutStderr: true };
  }

  const fileCapture = trySpawnMergedLogFile(command, env, cwd, signal, lifecycle);
  if (fileCapture) {
    return fileCapture;
  }

  const scriptCapture =
    process.platform === 'win32'
      ? trySpawnShellScriptLogRedirectWin(command, env, cwd, signal, lifecycle)
      : trySpawnShellScriptLogRedirectUnix(command, env, cwd, signal, lifecycle);
  if (scriptCapture) {
    return scriptCapture;
  }

  try {
    return trySpawnInheritStdio(command, env, cwd, signal, lifecycle);
  } catch (e) {
    throw lastErr instanceof Error ? lastErr : e instanceof Error ? e : new Error(String(e));
  }
}
